import { useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { API_CONFIG } from "../constants";
import "./ContactPage.css";

function ContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(""); // "sending" | "sent" | "error"

  const [validationError, setValidationError] = useState("");

  const validateEmail = (emailStr) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setValidationError("");
      setStatus("");

      // Validation
      if (!name.trim()) {
        setValidationError(t("contact.errorName"));
        return;
      }
      if (!email.trim() || !validateEmail(email)) {
        setValidationError(t("contact.errorEmail"));
        return;
      }
      if (!subject.trim()) {
        setValidationError(t("contact.errorSubject"));
        return;
      }
      if (!message.trim()) {
        setValidationError(t("contact.errorMessage"));
        return;
      }
      if (message.trim().length > 5000) {
        setValidationError(t("contact.errorMessageLength"));
        return;
      }

      setStatus("sending");

      try {
        // Send via backend API (hides the email from the client)
        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CONTACT}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: name.trim(),
              email: email.trim(),
              subject: subject.trim(),
              message: message.trim(),
              website: "",
            }),
          },
        );

        if (response.ok) {
          setStatus("sent");
          setName("");
          setEmail("");
          setSubject("");
          setMessage("");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    },
    [name, email, subject, message, t],
  );

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact - RenuAirHub</title>
        <meta
          name="description"
          content="Contact RenuAirHub — get in touch with our team for questions, feedback, or partnership inquiries."
        />
        <link rel="canonical" href="https://renuairhub.com/contact" />
      </Helmet>

      <div className="contact-header">
        <button className="contact-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" />
        </button>
        <h1 className="contact-logo">RenuAirHub</h1>
      </div>

      <div className="contact-content">
        <h2>{t("contact.title")}</h2>
        <p className="contact-subtitle">{t("contact.subtitle")}</p>

        {status === "sent" ? (
          <div className="contact-success">
            <p>{t("contact.success")}</p>
            <button onClick={() => navigate("/dashboard")}>
              {t("contact.backToDashboard")}
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Honeypot field — hidden from users, catches bots */}
            <input
              name="website"
              style={{ display: "none" }}
              tabIndex="-1"
              autoComplete="off"
            />
            <div className="contact-field">
              <label htmlFor="contact-name">{t("contact.name")}</label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-email">{t("contact.email")}</label>
              <input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-subject">{t("contact.subject")}</label>
              <input
                id="contact-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-message">{t("contact.message")}</label>
              <textarea
                id="contact-message"
                rows="5"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </div>

            {validationError && (
              <p className="contact-error">{validationError}</p>
            )}

            {status === "error" && (
              <p className="contact-error">{t("contact.error")}</p>
            )}

            <button
              type="submit"
              className="contact-submit"
              disabled={status === "sending"}
            >
              <SendIcon style={{ fontSize: 18 }} />
              <span>
                {status === "sending"
                  ? t("contact.sending")
                  : t("contact.send")}
              </span>
            </button>
          </form>
        )}
      </div>

      <div className="contact-footer">
        <p>
          © {new Date().getFullYear()} RenuAirHub. {t("about.allRights")}
        </p>
      </div>
    </div>
  );
}

export default ContactPage;
